import React, { useCallback, useState } from 'react';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { CONTENT_TYPE } from '@jwp/ott-common/src/constants';
import { MediaStatus, isLiveEvent } from '@jwp/ott-common/src/utils/liveEvent';
import { isLiveChannel } from '@jwp/ott-common/src/utils/media';
import { useWatchHistory } from '@jwp/ott-hooks-react/src/useWatchHistory';
import { usePlaylistItemCallback } from '@jwp/ott-hooks-react/src/usePlaylistItemCallback';
import { useAds } from '@jwp/ott-hooks-react/src/useAds';
import useProtectedMedia from '@jwp/ott-hooks-react/src/useProtectedMedia';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

import type { JWPlayer } from '../../../types/jwplayer';
import CustomPlayer, { type CustomPlayerController } from '../../components/CustomPlayer/CustomPlayer';
import Player from '../../components/Player/Player';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import PlayerError, { PlayerErrorState } from '../../components/PlayerError/PlayerError';

type Props = {
  item: PlaylistItem;
  seriesItem?: PlaylistItem;
  onPlay?: () => void;
  onPause?: () => void;
  onComplete?: () => void;
  onClose?: () => void;
  onUserActive?: () => void;
  onUserInActive?: () => void;
  onNext?: () => void;
  onAdBreakStart?: () => void;
  onAdBreakEnd?: () => void;
  feedId?: string;
  liveStartDateTime?: string | null;
  liveEndDateTime?: string | null;
  liveFromBeginning?: boolean;
  autostart?: boolean;
  nextItem?: PlaylistItem;
  showMoreLikeThisButton?: boolean;
  relatedVideos?: PlaylistItem[];
  onRelatedVideoClick?: (item: PlaylistItem) => void;
  ReConnecting : string
};

const PlayerContainer: React.FC<Props> = ({
  item,
  seriesItem,
  feedId,
  onPlay,
  onPause,
  onComplete,
  onClose,
  onUserActive,
  onUserInActive,
  onNext,
  onAdBreakStart,
  onAdBreakEnd,
  liveEndDateTime,
  liveFromBeginning,
  liveStartDateTime,
  autostart,
  nextItem,
  showMoreLikeThisButton,
  relatedVideos,
  onRelatedVideoClick,
  ReConnecting
}: Props) => {
  const isVodCustomPlayerEligible = (i: PlaylistItem) =>
    i.mediaStatus !== MediaStatus.LIVE &&
    !isLiveEvent(i) &&
    !isLiveChannel(i) &&
    i.contentType?.toLowerCase() !== CONTENT_TYPE.live &&
    i.contentType?.toLowerCase() !== CONTENT_TYPE.liveChannel;

  /** Shaka path: on-demand, or live / live channel when custom player is enabled. */
  const isCustomShakaEligible = (i: PlaylistItem) =>
    isVodCustomPlayerEligible(i) ||
    isLiveChannel(i) ||
    isLiveEvent(i) ||
    i.mediaStatus === MediaStatus.LIVE ||
    i.contentType?.toLowerCase() === CONTENT_TYPE.live;

  // data
  const { data: adsData, isLoading: isAdsLoading } = useAds({ mediaId: item?.mediaid, item });
  const { data: playableItem, isLoading, isGeoBlocked } = useProtectedMedia(item);
  const { canShowSettings, isAutoPlay, playerType } = useAppConfig();
  const customShakaEnabled = playerType?.toLowerCase() === 'custom';
  const params = new URLSearchParams(window.location.search);
  const shouldInitializePlayer = params.get('play') === '1';

  const breakpoint = useBreakpoint();

  const isMobile = breakpoint === Breakpoint.xs;
  const isTablet = breakpoint === Breakpoint.sm || breakpoint === Breakpoint.md;
  const isLaptop = breakpoint === Breakpoint.lg || breakpoint === Breakpoint.xl;

  const getResponsiveImage = (item: PlaylistItem) => {
    try {
      if (!item?.images || item.images.length === 0) {
        return item.image ? item.image : '';
      }

      let targetWidth = 1920;

      if (isMobile) {
        targetWidth = 320;
      } else if (isTablet) {
        targetWidth = 720;
      } else if (isLaptop) {
        targetWidth = 1920;
      }

      const selectedImage = item.images.reduce((prev, curr) => {
        return Math.abs(curr.width - targetWidth) < Math.abs(prev.width - targetWidth) ? curr : prev;
      });

      return selectedImage?.src || item.image;
    } catch (error) {
      console.error('error', error);
    }
  };

  // state
  const [playerInstance, setPlayerInstance] = useState<JWPlayer | CustomPlayerController>();

  // watch history
  const startTime = useWatchHistory(playerInstance, item, seriesItem);

  // player events
  const handleReady = useCallback((player?: JWPlayer | CustomPlayerController) => {
    setPlayerInstance(player);
  }, []);

  const handleFirstFrame = useCallback(() => {
    // when playing a livestream, the first moment we can seek to the beginning of the DVR range is after the
    // firstFrame event.
    // @todo this doesn't seem to work 100% out of the times. Confirm with player team if this is the best approach.
    if (liveFromBeginning) {
      playerInstance?.seek(0);
    }
  }, [liveFromBeginning, playerInstance]);

  const handlePlaylistItemCallback = usePlaylistItemCallback(liveStartDateTime, liveEndDateTime);

  if (!playableItem || isLoading || isAdsLoading) {
    return <LoadingOverlay inline />;
  }

  if (isGeoBlocked) {
    return <PlayerError error={PlayerErrorState.GEO_BLOCKED} />;
  }

  const isAutoPlayNextVideo = canShowSettings && isAutoPlay && localStorage.getItem('autoplay') ? localStorage.getItem('autoplay') === 'true' : false;

  if (!shouldInitializePlayer) {
    return null;
  }

  /** Controlled by Firebase remote config `platforms.web.player_type` (`custom` | `jwp`). */
  const useShakaPath = customShakaEnabled;
 
  return useShakaPath ? (
    <CustomPlayer
      feedId={feedId}
      item={playableItem}
      adsData={adsData}
      onReady={handleReady}
      onFirstFrame={handleFirstFrame}
      onPlay={onPlay}
      onPause={onPause}
      onComplete={onComplete}
      onUserActive={onUserActive}
      onUserInActive={onUserInActive}
      onNext={onNext}
      onAdBreakStart={onAdBreakStart}
      onAdBreakEnd={onAdBreakEnd}
      startTime={startTime}
      autostart={autostart}
      videoThumnail={getResponsiveImage(playableItem)}
      nextItem={nextItem}
      showMoreLikeThisButton={showMoreLikeThisButton}
      relatedVideos={relatedVideos}
      onRelatedVideoClick={onRelatedVideoClick}
      isAutoPlayNextVideo={isAutoPlayNextVideo}
      ReConnecting={ReConnecting}
    />
  ) : (
    <Player
      feedId={feedId}
      item={playableItem}
      adsData={adsData}
      onReady={handleReady}
      onFirstFrame={handleFirstFrame}
      onPlay={onPlay}
      onPause={onPause}
      onComplete={onComplete}
      onClose={onClose}
      onUserActive={onUserActive}
      onUserInActive={onUserInActive}
      onNext={onNext}
      onPlaylistItemCallback={handlePlaylistItemCallback}
      startTime={startTime}
      autostart={autostart}
      videoThumnail={getResponsiveImage(playableItem)}
      nextItem={nextItem}
      isAutoPlayNextVideo={isAutoPlayNextVideo}
      ReConnecting={ReConnecting}
    />
  );
};

export default PlayerContainer;
